import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar';
import logoCentrao from './assets/logo-centrao.png';
import Login from './pages/Login';
import TrocarSenhaObrigatorio from './pages/TrocarSenhaObrigatorio';
import Dashboard from './pages/Dashboard';
import MeuPerfil from './pages/MeuPerfil';
import Usuarios from './pages/Usuarios';
import Perfis from './pages/Perfis';
import Arquitetos from './pages/Arquitetos';
import Comissoes from './pages/Comissoes';
import Catalogo from './pages/Catalogo';
import ProdutoCatalogo from './pages/ProdutoCatalogo';
import CatalogoDestaques from './pages/CatalogoDestaques';
import GestaoCatalogo from './pages/GestaoCatalogo';
import Especificacoes from './pages/Especificacoes';
import EspecificacaoDetalhe from './pages/EspecificacaoDetalhe';
import EspecificacaoPdf from './pages/EspecificacaoPdf';
import { EspecificacaoProvider } from './contexts/EspecificacaoContext';
import EspecificacaoOverlays from './components/especificacao/EspecificacaoOverlays';
import SolicitarSaque from './pages/SolicitarSaque';
import AuditoriaSaques from './pages/AuditoriaSaques';
import SaldosArquitetos from './pages/SaldosArquitetos';
// Aprovação de saques migrou pro portal-financeiro (mai/2026). Rota e tela
// removidas deste portal. Quem aprova entra em financeiro.centraomateriais.com
import './index.css';

// Bloqueio global: arquiteto com senha_provisoria=true só acessa /trocar-senha
function SenhaProvisoriaGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user?.senha_provisoria && location.pathname !== '/trocar-senha') {
    return <Navigate to="/trocar-senha" replace />;
  }
  return children;
}

// Guard único de rota (espelha o padrão do sistema-rh): TODAS as rotas internas
// usam ESTE mesmo componente. Como o wrapper é sempre o mesmo na posição raiz do
// `element`, o React reconcilia o AppLayout/Sidebar entre navegações em vez de
// re-montar — então o menu não "pisca"/fecha ao clicar (o hover do rail persiste).
//
// Props:
//   - permissao: string | string[]  → exige UMA das permissões (uso interno; arquiteto nunca entra)
//   - somenteArquiteto: bool         → só usuários tipo 'arquiteto'
//   - comissoes: bool                → arquiteto (vê as suas) OU interno com 'comissoes_listar'
function PrivateRoute({ children, permissao, somenteArquiteto, comissoes, catalogo }) {
  const { user, temPermissao } = useAuth();
  if (!user) return <Navigate to="/login" replace />;

  const ehArquiteto = user.tipo === 'arquiteto';

  if (somenteArquiteto && !ehArquiteto) return <Navigate to="/dashboard" replace />;

  // Catálogo: arquiteto só entra se a diretoria já liberou (flag no token)
  if (catalogo && ehArquiteto && !user.catalogo_visivel) return <Navigate to="/dashboard" replace />;

  if (comissoes && !ehArquiteto && !temPermissao('comissoes_listar'))
    return <Navigate to="/dashboard" replace />;

  if (permissao) {
    const lista = Array.isArray(permissao) ? permissao : [permissao];
    if (ehArquiteto || !lista.some(p => temPermissao(p)))
      return <Navigate to="/dashboard" replace />;
  }

  return <SenhaProvisoriaGuard>{children}</SenhaProvisoriaGuard>;
}

function AppLayout({ children }) {
  return (
    <div className="layout">
      <Sidebar />
      {/* Logo fixa no canto superior direito (experimento) — não bloqueia cliques */}
      <img src={logoCentrao} alt="Centrão Materiais de Construção" className="app-logo-canto" />
      <main className="main-content">{children}</main>
      {/* Cesta da especificação, "+ Especificar" e avisos — valem em qualquer tela */}
      <EspecificacaoOverlays />
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={user
          ? <Navigate to={user.senha_provisoria ? '/trocar-senha' : '/dashboard'} replace />
          : <Login />}
      />
      <Route
        path="/trocar-senha"
        element={user
          ? <TrocarSenhaObrigatorio />
          : <Navigate to="/login" replace />}
      />
      <Route path="/dashboard" element={<PrivateRoute><AppLayout><Dashboard /></AppLayout></PrivateRoute>} />
      <Route path="/meu-perfil" element={<PrivateRoute><AppLayout><MeuPerfil /></AppLayout></PrivateRoute>} />
      <Route path="/usuarios" element={<PrivateRoute permissao="usuarios_listar"><AppLayout><Usuarios /></AppLayout></PrivateRoute>} />
      <Route path="/perfis" element={<PrivateRoute permissao="perfis_gerenciar"><AppLayout><Perfis /></AppLayout></PrivateRoute>} />
      <Route path="/arquitetos" element={<PrivateRoute permissao={['arquitetos_listar', 'arquitetos_vincular', 'arquitetos_editar']}><AppLayout><Arquitetos /></AppLayout></PrivateRoute>} />
      <Route path="/comissoes" element={<PrivateRoute comissoes><AppLayout><Comissoes /></AppLayout></PrivateRoute>} />
      <Route path="/saldos-arquitetos" element={<PrivateRoute permissao="saldos_arquitetos"><AppLayout><SaldosArquitetos /></AppLayout></PrivateRoute>} />
      <Route path="/catalogo" element={<PrivateRoute catalogo><AppLayout><Catalogo /></AppLayout></PrivateRoute>} />
      <Route path="/catalogo/:codpro" element={<PrivateRoute catalogo><AppLayout><ProdutoCatalogo /></AppLayout></PrivateRoute>} />
      <Route path="/catalogo-destaques" element={<PrivateRoute permissao="catalogo_destaques"><AppLayout><CatalogoDestaques /></AppLayout></PrivateRoute>} />
      <Route path="/catalogo-gestao" element={<PrivateRoute permissao="catalogo_destaques"><AppLayout><GestaoCatalogo /></AppLayout></PrivateRoute>} />
      <Route path="/especificacoes" element={<PrivateRoute catalogo><AppLayout><Especificacoes /></AppLayout></PrivateRoute>} />
      <Route path="/especificacoes/:id" element={<PrivateRoute catalogo><AppLayout><EspecificacaoDetalhe /></AppLayout></PrivateRoute>} />
      {/* Documento pra imprimir/salvar em PDF: tela limpa, sem menu */}
      <Route path="/especificacoes/:id/pdf" element={<PrivateRoute catalogo><EspecificacaoPdf /></PrivateRoute>} />
      <Route path="/saques" element={<PrivateRoute somenteArquiteto><AppLayout><SolicitarSaque /></AppLayout></PrivateRoute>} />
      <Route path="/auditoria-saques" element={<PrivateRoute permissao="auditoria_saques"><AppLayout><AuditoriaSaques /></AppLayout></PrivateRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <EspecificacaoProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </EspecificacaoProvider>
    </AuthProvider>
  );
}
